import { queryOptions, useQuery } from '@tanstack/react-query';
import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { FetchParams, IPagination } from '@/types/general.type';
import { TLabelCustomer } from '../types/label-customer.type';

export type TGetListLabelCustomerResponse = ApiResponse<IPagination<TLabelCustomer>>;

export const getListLabelCustomer = async (params: FetchParams): Promise<TGetListLabelCustomerResponse> => {
  const url = '/label-customer';

  return axiosInstance.get(url, { params });
};

export const GET_LIST_LABEL_CUSTOMER_QUERY_KEY = 'label-customer';

export const getListLabelCustomerQueryOptions = <TData = TGetListLabelCustomerResponse>(
  params: FetchParams,
  select?: (data: TGetListLabelCustomerResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_LABEL_CUSTOMER_QUERY_KEY,
      {
        page: params.page,
        limit: params.limit,
        search: params.search,
        sortDir: params.sortDir,
        sortBy: params.sortBy,
      },
    ],
    queryFn: () => getListLabelCustomer(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListLabelCustomerQuery<TData>(options: {
  params: FetchParams;
  queryConfig?: QueryConfig<typeof getListLabelCustomerQueryOptions<TData>>;
  select: (data: TGetListLabelCustomerResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListLabelCustomerQuery(options: {
  params: FetchParams;
  queryConfig?: QueryConfig<typeof getListLabelCustomerQueryOptions<TGetListLabelCustomerResponse>>;
}): ReturnType<typeof useQuery<TGetListLabelCustomerResponse>>;

// Implementation
export function useGetListLabelCustomerQuery<TData = TGetListLabelCustomerResponse>({
  params,
  queryConfig,
  select,
}: {
  params: FetchParams;
  queryConfig?: QueryConfig<typeof getListLabelCustomerQueryOptions<TData | TGetListLabelCustomerResponse>>;
  select?: (data: TGetListLabelCustomerResponse) => TData;
}) {
  return useQuery({
    ...getListLabelCustomerQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<TData extends TGetListLabelCustomerResponse ? TGetListLabelCustomerResponse : TData>
  >;
}
